import type {
  CanonicalPrivateEventMap,
  OrderCancelled,
  OrderFilled,
  OrderPlaced,
  PrivateEventName,
  TradeExecuted,
} from '@/lib/realtime/contracts';
import { computePlaeeEventId } from '@/lib/realtime/contracts';
import type { ClientErrorReport } from '@/lib/realtime/client-error-reporter';
import { clientErrorReporter as defaultReporter } from '@/lib/realtime/client-error-reporter';

/**
 * Context the adapter needs from the surrounding session — these fields are
 * not on the wire shape Plaee delivers.
 */
export interface AdaptContext {
  /** Internal user id (cuid2) — never present in raw Plaee payloads. */
  userId: string;
  /**
   * When the event "occurred". Plaee does not currently emit a server clock on
   * private events; the FE adapter uses the client's wall clock at receive
   * time as a stable substitute. The api-side handler does the same so both
   * paths converge.
   */
  occurredAt: string;
}

/** Shape the adapter consumes — Centrifuge publication-like. */
interface PublicationLike {
  offset: number | string;
  data: { type?: string } & Record<string, unknown>;
}

interface AdaptOptions {
  errorReporter?: { report(input: ClientErrorReport): void };
}

interface AdaptResult<E extends PrivateEventName> {
  name: E;
  plaeeEventId: string;
  payload: CanonicalPrivateEventMap[E];
}

type AnyAdaptResult = {
  [E in PrivateEventName]: AdaptResult<E>;
}[PrivateEventName];

function isObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object';
}

function asPublication(raw: unknown): PublicationLike | null {
  if (!isObject(raw)) return null;
  // Accept either a Centrifuge publication envelope (`{ offset, data }`) or a
  // bare data object — the RealtimeManager strips publications down to their
  // `data` before forwarding, while older Centrifuge call sites pass the full
  // ctx. Either shape projects to the same canonical event. Offset is only
  // used for diagnostic error reporting, so a missing value falls back to 0.
  const inner = (raw as { data?: unknown }).data;
  if (isObject(inner)) {
    const offset = (raw as { offset?: unknown }).offset;
    const off = typeof offset === 'number' || typeof offset === 'string' ? offset : 0;
    return { offset: off, data: inner as PublicationLike['data'] };
  }
  if (typeof (raw as { type?: unknown }).type === 'string') {
    return { offset: 0, data: raw as PublicationLike['data'] };
  }
  return null;
}

function lower(side: string): 'buy' | 'sell' {
  return side.toLowerCase() === 'sell' ? 'sell' : 'buy';
}

/**
 * Translates a raw Plaee Centrifugo publication into a canonical private
 * event. Returns null on unknown event types so the caller can skip ingest.
 *
 * On a recognised-but-malformed payload the function records a client error
 * (via the supplied or default {@link ClientErrorReporter}) and returns null.
 *
 * **The canonical shapes here MUST stay aligned with the api-side
 * `PlaeePrivateEventHandler` (5C).** See realtime-architecture.md
 * "Idempotency contract — both paths must agree on the same id".
 */
export function adapt(raw: unknown, ctx: AdaptContext, opts: AdaptOptions = {}): AnyAdaptResult | null {
  const pub = asPublication(raw);
  if (pub === null) return null;
  const reporter = opts.errorReporter ?? defaultReporter;

  try {
    switch (pub.data.type) {
      case 'order_updated': {
        const d = pub.data as Record<string, unknown>;
        const ordStatus = String(d.ordStatus ?? '').toUpperCase();
        const orderId = String(d.orderId);
        const marketId = String(d.marketId);
        const outcomeId = String(d.outcomeId);
        const sideRaw = String(d.side ?? '');
        if (!orderId || !marketId || !outcomeId || !sideRaw) {
          throw new Error('order_updated missing required fields');
        }
        const side = lower(sideRaw);
        // Plaee does not carry contractSide on private order WS payloads;
        // mirror the api-side handler's data-driven guess: SELL -> No, else Yes.
        // The two sides MUST agree on this default for cross-path dedup to work.
        const contractSide: 'Yes' | 'No' = sideRaw.toUpperCase() === 'SELL' ? 'No' : 'Yes';
        const price = Number(d.price);
        const quantity = Number(d.quantity);
        const cumQty = Number(d.cumQty);
        const leavesQty = Number(d.leavesQty);
        const naturalKey = `${orderId}|${ordStatus}|${cumQty}|${leavesQty}`;

        if (ordStatus === 'NEW') {
          const plaeeEventId = computePlaeeEventId({
            type: 'order_placed',
            userId: ctx.userId,
            naturalKey,
          });
          const payload: OrderPlaced = {
            plaeeEventId,
            userId: ctx.userId,
            occurredAt: ctx.occurredAt,
            orderId,
            marketId,
            outcomeId,
            side,
            contractSide,
            price,
            quantity,
            remaining: leavesQty,
          };
          return { name: 'order:placed', plaeeEventId, payload };
        }

        if (ordStatus === 'FILLED' || ordStatus === 'PARTIALLY_FILLED' || ordStatus === 'PARTIAL_FILL') {
          const partial = ordStatus !== 'FILLED' || leavesQty > 0;
          const plaeeEventId = computePlaeeEventId({
            type: partial ? 'order_partial_fill' : 'order_filled',
            userId: ctx.userId,
            naturalKey,
          });
          const payload: OrderFilled = {
            plaeeEventId,
            userId: ctx.userId,
            occurredAt: ctx.occurredAt,
            orderId,
            marketId,
            outcomeId,
            side,
            contractSide,
            fillPrice: price,
            fillQuantity: cumQty,
            remaining: leavesQty,
            status: partial ? 'partially-filled' : 'filled',
          };
          return {
            name: partial ? 'order:partially-filled' : 'order:filled',
            plaeeEventId,
            payload,
          };
        }

        if (ordStatus === 'CANCELED' || ordStatus === 'CANCELLED' || ordStatus === 'EXPIRED') {
          const plaeeEventId = computePlaeeEventId({
            type: 'order_cancelled',
            userId: ctx.userId,
            naturalKey,
          });
          const payload: OrderCancelled = {
            plaeeEventId,
            userId: ctx.userId,
            occurredAt: ctx.occurredAt,
            orderId,
            marketId,
            outcomeId,
            reason: ordStatus === 'EXPIRED' ? 'expired' : 'user',
          };
          return { name: 'order:cancelled', plaeeEventId, payload };
        }

        // Unrecognised ordStatus — treat as unknown rather than malformed.
        return null;
      }

      case 'trade_executed': {
        const d = pub.data as Record<string, unknown>;
        const tradeId = String(d.tradeReportId ?? '');
        const marketId = String(d.marketId ?? '');
        const outcomeId = String(d.outcomeId ?? '');
        const sideRaw = String(d.side ?? '');
        const price = Number(d.price);
        const quantity = Number(d.quantity);
        if (!tradeId || !marketId || !outcomeId) {
          throw new Error('trade_executed missing required fields');
        }
        const contractSide: 'Yes' | 'No' = sideRaw.toUpperCase() === 'SELL' ? 'No' : 'Yes';
        const plaeeEventId = computePlaeeEventId({
          type: 'trade_executed',
          userId: ctx.userId,
          naturalKey: `trade|${tradeId}`,
        });
        const payload: TradeExecuted = {
          plaeeEventId,
          userId: ctx.userId,
          occurredAt: ctx.occurredAt,
          tradeId,
          marketId,
          outcomeId,
          contractSide,
          price,
          quantity,
          role: 'taker',
        };
        return { name: 'trade:executed', plaeeEventId, payload };
      }

      case 'position_updated':
      case 'pnl_updated':
        // Plaee's WS for position/pnl is a nudge — only the webhook (path A)
        // carries the actual numbers. Per `DEFAULT_PRIVATE_POLICIES` these are
        // `backend-only`, so dropping them here is the correct behaviour:
        // there's nothing useful to project, and ingesting a zero-padded
        // canonical payload would just create noise the bus then filters out.
        return null;

      default:
        return null;
    }
  } catch (err) {
    reporter.report({
      kind: 'plaee-adapter-parse-failed',
      detail: {
        type: pub.data.type ?? 'unknown',
        message: err instanceof Error ? err.message : String(err),
        offset: pub.offset,
      },
    });
    return null;
  }
}
